import type { EntityState } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
} from '@core/ngrx-legacy';
import { InitialDataChecksumToken } from '@core/reports-types';

import type {
  FetchOneVisualizationFailedPayload,
  FetchOneVisualizationPayload,
  FetchOneVisualizationSuccessPayload,
  ReportsVisualizationEntity,
} from './visualizations.types';
import { safeReportsStateSelector } from '../helpers/safe-selector';

const selectDataId = (data: { paramsHash: string }) => data.paramsHash;

const initialData = (): ReportsVisualizationEntity => ({
  paramsHash: InitialDataChecksumToken,
  response: {
    checksum: InitialDataChecksumToken,
    meta: {},
    results: {
      columns: [],
      rows: [],
    },
  },
});

const { actions, selectors, reducer, namespace, initialState } =
  createEntityCollectionStore<ReportsVisualizationEntity>()('ReportsVisualizations', {
    idSelector: selectDataId,
    baseSelector: (root): EntityState<ReportsVisualizationEntity> =>
      safeReportsStateSelector(root, namespace, initialState),

    customActions: adapter => {
      const {
        createOneOptimistic,
        updateOneOptimistic,
        createOneOptimisticSuccess,
        createOneOptimisticFailed,
      } = getEntityCollectionActions(adapter, selectDataId);

      return {
        updateVisualization: ngrxEvent<any>(),
        fetchOneVisualization: (state, payload: FetchOneVisualizationPayload) =>
          state.entityMap[payload.data.paramsHash]
            ? updateOneOptimistic(state, {
                correlationId: payload.data.paramsHash,
                data: state.entityMap[payload.data.paramsHash]!,
              })
            : createOneOptimistic(state, {
                correlationId: payload.data.paramsHash,
                data: initialData(),
              }),
        fetchOneVisualizationSuccess: (state, payload: FetchOneVisualizationSuccessPayload) =>
          createOneOptimisticSuccess(state, {
            correlationId: payload.data.paramsHash,
            data: payload.data,
          }),

        fetchOneVisualizationFailed: (state, payload: FetchOneVisualizationFailedPayload) =>
          createOneOptimisticFailed(state, {
            correlationId: payload.data.paramsHash,
            data: <any>{
              paramsHash: payload.data.paramsHash,
            },
            error: <any>payload.data.error,
          }),
      };
    },
  });

const selectVisualizationDataById =
  (id: string) =>
  (state: EntityState<ReportsVisualizationEntity>): ReportsVisualizationEntity =>
    selectors.byId(id)(state) ?? initialData();

export const ReportsVisualizationsSelectors = {
  byId: selectVisualizationDataById,
  meta: selectors.meta,
};

export {
  actions as ReportsVisualizationsActions,
  reducer as ReportsVisualizationsReducer,
  namespace as ReportsVisualizationsNamespace,
};
